import React, { useEffect, useState } from 'react';
import { Search, CheckCircle2, XCircle, Clock, FileText, ArrowUpRight, Menu, HelpCircle, HomeIcon, Users, ClipboardList, Eye, Shield, FileCheck, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const ROWS = [{
  name: 'Dunder Mifflin',
  app: 'Customer Credit Application',
  status: 'Ready for Review',
  icon: Search,
  color: 'text-[#6C5CE7]',
  date: 'Oct 24, 2023',
  refs: [1, 1, 1],
  guarantors: [1, 1]
}, {
  name: 'Wayne Enterprises',
  app: 'Business Credit Application',
  status: 'In Progress',
  icon: Clock,
  color: 'text-gray-500',
  date: 'Oct 22, 2023',
  refs: [-1, -1, -1],
  guarantors: [-1, -1]
}, {
  name: 'Wally Wonka',
  app: 'Credit Application',
  status: 'Rejected',
  icon: XCircle,
  color: 'text-red-500',
  date: 'Oct 20, 2023',
  refs: [1, 0, -1],
  guarantors: [1, 0]
}, {
  name: 'Acme Corporation',
  app: 'Customer Credit Application',
  status: 'Submitted',
  icon: FileText,
  color: 'text-blue-500',
  date: 'Oct 18, 2023',
  refs: [0, 0, 0],
  guarantors: [0]
}];
export function ApplicationHub({
  onComplete
}: {
  onComplete?: () => void;
}) {
  const [visibleRows, setVisibleRows] = useState(0);
  const [showPulse, setShowPulse] = useState(false);
  const [showReview, setShowReview] = useState(false);
  useEffect(() => {
    const rowTimers: ReturnType<typeof setTimeout>[] = [];
    for (let i = 0; i <= ROWS.length; i++) {
      rowTimers.push(setTimeout(() => setVisibleRows(i + 1), 300 + i * 375));
    }
    const pulseTimer = setTimeout(() => setShowPulse(true), 3300);
    const reviewTimer = setTimeout(() => {
      setShowPulse(false);
      setShowReview(true);
    }, 5500);
    // Call onComplete after review panel has been shown
    const completeTimer = setTimeout(() => {
      if (onComplete) onComplete();
    }, 8500);
    return () => {
      rowTimers.forEach(clearTimeout);
      clearTimeout(pulseTimer);
      clearTimeout(reviewTimer);
      clearTimeout(completeTimer);
    };
  }, []);
  return <div className="w-full h-full bg-white rounded-xl overflow-hidden flex flex-col text-sm border border-gray-200/50" data-id="element-136">
      {/* Product Header */}
      <div className="flex items-center gap-3 px-4 py-2 border-b border-gray-200 bg-white shrink-0" data-id="element-137">
        <Menu className="w-4 h-4 text-gray-500" data-id="element-138" />
        <img src="https://cdn.magicpatterns.com/uploads/a5S8ZQgbN9jzaBrUDmr99x/Logo_CreditPulse_Black.png" alt="Credit Pulse" className="h-5 object-contain" data-id="element-139" />
        <div className="flex-1 mx-3 relative" data-id="element-140">
          <input type="text" placeholder="Search Companies" className="w-full pl-3 pr-8 py-1 text-xs border border-gray-200 rounded bg-gray-50 focus:outline-none" data-id="element-141" />
          <Search className="w-3 h-3 text-gray-400 absolute right-2.5 top-1/2 -translate-y-1/2" data-id="element-142" />
        </div>
        <HelpCircle className="w-4 h-4 text-gray-400" data-id="element-143" />
        <div className="w-5 h-5 rounded-full bg-gray-300 flex items-center justify-center text-[8px] font-bold text-white" data-id="element-144">
          SU
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden" data-id="element-145">
        {/* Left Sidebar */}
        <div className="w-10 bg-white border-r border-gray-200 flex flex-col items-center py-3 gap-3.5 shrink-0" data-id="element-146">
          <HomeIcon className="w-4 h-4 text-gray-400" data-id="element-147" />
          <Users className="w-4 h-4 text-gray-400" data-id="element-148" />
          <div className="w-7 h-7 rounded-md bg-[#6C5CE7]/10 flex items-center justify-center" data-id="element-149">
            <ClipboardList className="w-4 h-4 text-[#6C5CE7]" data-id="element-150" />
          </div>
          <Eye className="w-4 h-4 text-gray-400" data-id="element-151" />
          <Shield className="w-4 h-4 text-gray-400" data-id="element-152" />
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden" data-id="element-153">
          {/* Page Header */}
          <div className="px-4 py-2.5 border-b border-gray-200 flex justify-between items-center bg-gray-50/50 shrink-0" data-id="element-154">
            <h2 className="text-sm font-semibold text-gray-900" data-id="element-155">
              Application Hub
            </h2>
            <div className="flex gap-2" data-id="element-156">
              <button className="px-2 py-1 text-[10px] font-medium text-gray-600 border border-gray-300 rounded bg-white hover:bg-gray-50 transition-colors" data-id="element-157">
                More Actions
              </button>
              <button className="px-2 py-1 text-[10px] font-medium text-[#6C5CE7] border border-[#6C5CE7]/40 rounded bg-[#6C5CE7]/10 hover:bg-[#6C5CE7]/20 transition-colors" data-id="element-158">
                Application Links
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-2.5 px-4 py-2.5 bg-gray-50/30 border-b border-gray-200 shrink-0" data-id="element-159">
            <div className="bg-blue-50/50 p-2.5 rounded-lg border-l-4 border-l-blue-400 border-y border-r border-gray-200 shadow-sm flex flex-col items-center justify-center" data-id="element-160">
              <span className="text-[9px] text-gray-500 font-medium mb-0.5 uppercase tracking-wider" data-id="element-161">
                Total Applications
              </span>
              <div className="flex items-center gap-1.5" data-id="element-162">
                <span className="text-lg font-bold text-gray-900" data-id="element-163">12</span>
                <span className="text-[9px] text-green-700 bg-green-100/80 px-1 py-0.5 rounded flex items-center" data-id="element-164">
                  <ArrowUpRight className="w-2 h-2 mr-0.5" data-id="element-165" /> 24%
                </span>
              </div>
            </div>
            <div className="bg-green-50/50 p-2.5 rounded-lg border-l-4 border-l-green-400 border-y border-r border-gray-200 shadow-sm flex flex-col items-center justify-center" data-id="element-166">
              <span className="text-[9px] text-gray-500 font-medium mb-0.5 uppercase tracking-wider" data-id="element-167">
                % Approved
              </span>
              <div className="flex items-center gap-1.5" data-id="element-168">
                <span className="text-lg font-bold text-gray-900" data-id="element-169">67%</span>
                <span className="text-[9px] text-green-700 bg-green-100/80 px-1 py-0.5 rounded flex items-center" data-id="element-170">
                  <ArrowUpRight className="w-2 h-2 mr-0.5" data-id="element-171" /> 5%
                </span>
              </div>
            </div>
            <div className="bg-[#6C5CE7]/[0.04] p-2.5 rounded-lg border-l-4 border-l-[#6C5CE7] border-y border-r border-gray-200 shadow-sm flex flex-col items-center justify-center" data-id="element-172">
              <span className="text-[9px] text-gray-500 font-medium mb-0.5 uppercase tracking-wider" data-id="element-173">
                Credit Approved
              </span>
              <span className="text-lg font-bold text-gray-900" data-id="element-174">$450K</span>
            </div>
          </div>

          {/* Filters */}
          <div className="px-4 py-2 border-b border-gray-200 shrink-0" data-id="element-175">
            <div className="flex gap-1.5 overflow-x-auto scrollbar-hide" data-id="element-176">
              <button className="px-2.5 py-1 text-[10px] font-medium text-white bg-[#0F1117] rounded-md whitespace-nowrap shadow-sm" data-id="element-177">
                All Applications
              </button>
              <button className="px-2.5 py-1 text-[10px] font-medium text-gray-600 bg-white border border-gray-200 rounded-md whitespace-nowrap" data-id="element-178">
                In Progress (3)
              </button>
              <button className="px-2.5 py-1 text-[10px] font-medium text-gray-600 bg-white border border-gray-200 rounded-md whitespace-nowrap" data-id="element-179">
                Awaiting Signature (1)
              </button>
              <button className="px-2.5 py-1 text-[10px] font-medium text-gray-600 bg-white border border-gray-200 rounded-md whitespace-nowrap" data-id="element-180">
                Submitted (2)
              </button>
              <button className="px-2.5 py-1 text-[10px] font-medium text-gray-600 bg-white border border-gray-200 rounded-md whitespace-nowrap" data-id="element-181">
                Awaiting Responses (4)
              </button>
              <button className="px-2.5 py-1 text-[10px] font-medium text-gray-600 bg-white border border-gray-200 rounded-md whitespace-nowrap" data-id="element-182">
                Ready for Review (2)
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="flex-1 overflow-hidden" data-id="element-183">
            <table className="w-full text-left border-collapse" data-id="element-184">
              <thead data-id="element-185">
                <tr className="border-b border-gray-200 bg-gray-50/50" data-id="element-186">
                  <th className="px-3 py-1.5 text-[9px] font-medium text-gray-500 uppercase tracking-wider" data-id="element-187">
                    Submitted By
                  </th>
                  <th className="px-3 py-1.5 text-[9px] font-medium text-gray-500 uppercase tracking-wider" data-id="element-188">
                    Application
                  </th>
                  <th className="px-3 py-1.5 text-[9px] font-medium text-gray-500 uppercase tracking-wider" data-id="element-189">
                    Status
                  </th>
                  <th className="px-3 py-1.5 text-[9px] font-medium text-gray-500 uppercase tracking-wider" data-id="element-190">
                    Trade Refs
                  </th>
                  <th className="px-3 py-1.5 text-[9px] font-medium text-gray-500 uppercase tracking-wider" data-id="element-191">
                    Guarantors
                  </th>
                  <th className="px-3 py-1.5 text-[9px] font-medium text-gray-500 uppercase tracking-wider" data-id="element-192">
                    Updated
                  </th>
                  <th className="px-3 py-1.5 text-[9px] font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-193">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-194">
                {ROWS.map((row, i) => {
                const isVisible = i < visibleRows;
                const isFirst = i === 0;
                const isFlagged = !!(row as any).flagged;
                const rowDelay = 0.2 + i * 0.25;
                const bgColor = isFlagged && isVisible ? 'rgba(239, 68, 68, 0.06)' : isFirst && showReview ? 'rgba(108, 92, 231, 0.06)' : 'rgba(255, 255, 255, 0)';
                return <AnimatePresence key={i} data-id="element-195">
                      {isVisible && <motion.tr initial={{
                    opacity: 0,
                    y: 12
                  }} animate={{
                    opacity: 1,
                    y: 0,
                    backgroundColor: bgColor
                  }} transition={{
                    opacity: {
                      duration: 0.3
                    },
                    y: {
                      duration: 0.3
                    },
                    backgroundColor: {
                      duration: 0.4
                    }
                  }} className={isFlagged ? 'ring-1 ring-red-200 ring-inset' : isFirst && showReview ? 'ring-1 ring-[#6C5CE7]/20 ring-inset' : ''} data-id="element-196">
                          <td className="px-3 py-5 text-xs font-medium text-gray-900" data-id="element-197">
                            <div className="flex items-center gap-1.5" data-id="element-198">
                              {row.name}
                              {isFlagged && <motion.span initial={{
                          scale: 0,
                          opacity: 0
                        }} animate={{
                          scale: 1,
                          opacity: 1
                        }} transition={{
                          delay: rowDelay + 0.5,
                          type: 'spring',
                          stiffness: 300,
                          damping: 20
                        }} className="px-1.5 py-0.5 text-[7px] font-bold uppercase tracking-wider bg-red-100 text-red-700 rounded border border-red-200" data-id="element-199">
                                  Fraudulent
                                </motion.span>}
                            </div>
                          </td>
                          <td className="px-3 py-5 text-xs text-gray-500" data-id="element-200">
                            {row.app}
                          </td>
                          <td className="px-3 py-5" data-id="element-201">
                            <div className="flex items-center gap-1" data-id="element-202">
                              <row.icon className={`w-3 h-3 ${row.color}`} data-id="element-203" />
                              <span className={`text-xs ${isFlagged ? 'text-red-600 font-medium' : 'text-gray-700'}`} data-id="element-204">
                                {row.status}
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-5" data-id="element-205">
                            <div className="flex gap-0.5" data-id="element-206">
                              {row.refs.map((ref, j) => <motion.div key={j} initial={{
                          scale: 0,
                          opacity: 0
                        }} animate={{
                          scale: 1,
                          opacity: 1
                        }} transition={{
                          delay: rowDelay + 0.15 + j * 0.12,
                          type: 'spring',
                          stiffness: 400,
                          damping: 20
                        }} className={`w-2.5 h-2.5 rounded-sm ${ref === 1 ? 'bg-green-500' : ref === 0 ? 'bg-amber-400' : 'bg-gray-200'}`} data-id="element-207" />)}
                            </div>
                          </td>
                          <td className="px-3 py-5" data-id="element-208">
                            <div className="flex gap-0.5" data-id="element-209">
                              {row.guarantors.map((g, j) => <motion.div key={j} initial={{
                          scale: 0,
                          opacity: 0
                        }} animate={{
                          scale: 1,
                          opacity: 1
                        }} transition={{
                          delay: rowDelay + 0.3 + j * 0.12,
                          type: 'spring',
                          stiffness: 400,
                          damping: 20
                        }} className={`w-2.5 h-2.5 rounded-sm ${g === 1 ? 'bg-green-500' : g === 0 ? 'bg-amber-400' : 'bg-gray-200'}`} data-id="element-210" />)}
                            </div>
                          </td>
                          <td className="px-3 py-5 text-xs text-gray-500" data-id="element-211">
                            {row.date}
                          </td>
                          <td className="px-3 py-5 text-right" data-id="element-212">
                            {isFirst && showPulse ? <motion.button animate={{
                        scale: [1, 1.08, 1]
                      }} transition={{
                        duration: 1.2,
                        repeat: Infinity,
                        ease: 'easeInOut'
                      }} className="px-2 py-0.5 text-[10px] font-medium text-white bg-[#6C5CE7] rounded shadow-md ring-2 ring-[#6C5CE7]/30" data-id="element-213">
                                VIEW
                              </motion.button> : <button className="px-2 py-0.5 text-[10px] font-medium text-white bg-[#6C5CE7] rounded shadow-sm" data-id="element-214">
                                VIEW
                              </button>}
                          </td>
                        </motion.tr>}
                    </AnimatePresence>;
              })}
              </tbody>
            </table>
          </div>

          {/* Slide-up Review Panel */}
          <AnimatePresence data-id="element-215">
            {showReview && <motion.div initial={{
            y: '100%'
          }} animate={{
            y: 0
          }} exit={{
            y: '100%'
          }} transition={{
            type: 'spring',
            damping: 25,
            stiffness: 200
          }} className="absolute bottom-0 left-0 right-0 h-[60%] bg-white rounded-t-xl shadow-[0_-10px_40px_rgba(0,0,0,0.15)] border border-gray-300 z-20 flex flex-col overflow-hidden" data-id="element-216">
                {/* Panel Header */}
                <div className="flex items-center justify-between px-5 pt-3 pb-2.5 border-b border-gray-200 shrink-0" data-id="element-217">
                  <div className="flex items-center gap-3" data-id="element-218">
                    <div className="w-1 h-7 bg-[#6C5CE7] rounded-full" data-id="element-219"></div>
                    <div data-id="element-220">
                      <h3 className="text-sm font-bold text-gray-900" data-id="element-221">
                        Dunder Mifflin Paper Company
                      </h3>
                      <div className="flex items-center gap-2 mt-0.5" data-id="element-222">
                        <span className="px-2 py-0.5 bg-[#6C5CE7]/10 text-[#6C5CE7] text-[10px] font-medium rounded-full border border-[#6C5CE7]/20 flex items-center gap-1" data-id="element-223">
                          <Search className="w-2.5 h-2.5" data-id="element-224" /> Ready for Review
                        </span>
                        <span className="text-[10px] text-gray-400" data-id="element-225">
                          Customer Credit Application
                        </span>
                      </div>
                    </div>
                  </div>
                  <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[#6C5CE7] rounded-lg hover:bg-[#6C5CE7]/90 transition-colors shadow-sm" data-id="element-226">
                    <Zap className="w-3 h-3" data-id="element-227" /> Match & Decision
                  </button>
                </div>

                <div className="p-4 flex-1 overflow-hidden" data-id="element-228">
                  <div className="grid grid-cols-2 gap-4 h-full" data-id="element-229">
                    {/* Left: Applicant Details */}
                    <div className="border border-gray-200 rounded-lg p-4 flex flex-col" data-id="element-230">
                      <h4 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-231">
                        Applicant Details
                      </h4>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-4 flex-1" data-id="element-232">
                        <div data-id="element-233">
                          <span className="text-[10px] text-gray-400 block mb-0.5" data-id="element-234">
                            Company
                          </span>
                          <span className="text-xs font-semibold text-gray-900" data-id="element-235">
                            Dunder Mifflin Paper Co.
                          </span>
                        </div>
                        <div data-id="element-236">
                          <span className="text-[10px] text-gray-400 block mb-0.5" data-id="element-237">
                            Applicant
                          </span>
                          <span className="text-xs font-semibold text-gray-900" data-id="element-238">
                            Michael Scott
                          </span>
                        </div>
                        <div data-id="element-239">
                          <span className="text-[10px] text-gray-400 block mb-0.5" data-id="element-240">
                            Role
                          </span>
                          <span className="text-xs font-semibold text-gray-900" data-id="element-241">
                            Regional Manager
                          </span>
                        </div>
                        <div data-id="element-242">
                          <span className="text-[10px] text-gray-400 block mb-0.5" data-id="element-243">
                            Email
                          </span>
                          <span className="text-xs font-semibold text-gray-900 flex items-center gap-1" data-id="element-244">
                            mscott@dundermifflin.com
                            <CheckCircle2 className="w-3 h-3 text-green-500" data-id="element-245" />
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Right: Trade Refs & PG */}
                    <div className="flex flex-col gap-2" data-id="element-246">
                      {/* Trade Ref 1 */}
                      <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg flex-1" data-id="element-247">
                        <div data-id="element-248">
                          <span className="text-xs font-semibold text-gray-900 block" data-id="element-249">
                            Keystone Paper Pulp
                          </span>
                          <span className="text-[10px] text-gray-500" data-id="element-250">
                            5yr relationship, net-30
                          </span>
                        </div>
                        <div className="flex items-center gap-1 px-2 py-0.5 bg-green-50 border border-green-200 rounded-full" data-id="element-251">
                          <CheckCircle2 className="w-3 h-3 text-green-600" data-id="element-252" />
                          <span className="text-[10px] font-bold text-green-700" data-id="element-253">
                            Verified
                          </span>
                        </div>
                      </div>

                      {/* Trade Ref 2 */}
                      <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg flex-1" data-id="element-254">
                        <div data-id="element-255">
                          <span className="text-xs font-semibold text-gray-900 block" data-id="element-256">
                            The Box Company
                          </span>
                          <span className="text-[10px] text-gray-500" data-id="element-257">
                            8yr relationship, on time
                          </span>
                        </div>
                        <div className="flex items-center gap-1 px-2 py-0.5 bg-green-50 border border-green-200 rounded-full" data-id="element-258">
                          <CheckCircle2 className="w-3 h-3 text-green-600" data-id="element-259" />
                          <span className="text-[10px] font-bold text-green-700" data-id="element-260">
                            Verified
                          </span>
                        </div>
                      </div>

                      {/* Personal Guarantee */}
                      <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg flex-1" data-id="element-261">
                        <div data-id="element-262">
                          <span className="text-xs font-semibold text-gray-900 block" data-id="element-263">
                            Michael G. Scott
                          </span>
                          <span className="text-[10px] text-gray-500" data-id="element-264">
                            Personal Guarantee
                          </span>
                        </div>
                        <div className="flex items-center gap-1 px-2 py-0.5 bg-green-50 border border-green-200 rounded-full" data-id="element-265">
                          <FileCheck className="w-3 h-3 text-green-600" data-id="element-266" />
                          <span className="text-[10px] font-bold text-green-700" data-id="element-267">
                            Signed
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </div>;
}